import React, { useState, useEffect } from "react";
import logo from "assets/img/logo.png";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import config from "config";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateBackendError } from "utils/errorUtils";
import PasswordStrengthBar from "components/PasswordStrength/PasswordStrengthBar";
import { isPasswordStrong, isPasswordTooLong, MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from "utils/passwordUtils";

// Chakra imports
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  Spinner,
  keyframes,
} from "@chakra-ui/react";
import MufynButton from "components/Button";

// Animations (same as SignIn)
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function ResetPassword() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const passwordTooShortMessage = t("password.error.too_short", {
    count: MIN_PASSWORD_LENGTH,
    defaultValue: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`
  });
  const passwordTooLongMessage = t("password.error.too_long", {
    count: MAX_PASSWORD_LENGTH,
    defaultValue: `Password must be at most ${MAX_PASSWORD_LENGTH} characters long`
  });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [token, setToken] = useState("");

  // Utiliser la langue du navigateur pour les pages publiques
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    
    if (!i18n.language || i18n.language === 'en') {
      i18n.changeLanguage(getBrowserLanguage());
    }
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    // Extract token from URL
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get("token");
    
    if (!resetToken) {
      toast({
        title: t("reset_password.error.title"),
        description: t("reset_password.error.invalid_token"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      history.push("/auth/signin");
      return;
    }

    setToken(resetToken);

    // Verify token validity
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/verify-reset-token?token=${resetToken}`);
        setTokenValid(true);
      } catch (error) {
        toast({
          title: t("reset_password.error.title"),
          description: t("reset_password.error.invalid_token"),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
        setTimeout(() => history.push("/auth/signin"), 2000);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [location, history, toast, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast({
        title: t("reset_password.error.title"),
        description: t("reset_password.error.fill_fields"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t("reset_password.error.title"),
        description: t("reset_password.error.password_mismatch"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (!isPasswordStrong(newPassword)) {
      const errorMessage = isPasswordTooLong(newPassword) ? passwordTooLongMessage : passwordTooShortMessage;
      toast({
        title: t("reset_password.error.title"),
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${config.apiUrl}/reset-password`, {
        token,
        newPassword,
      });
      
      if (!response.data || !response.data.message) {
        throw new Error("Password reset failed - no response");
      }
      
      toast({
        title: t("reset_password.success.title"),
        description: t("reset_password.success.description"),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      
      // Redirect to login after 2 seconds
      setTimeout(() => history.push("/auth/signin"), 2000);
    } catch (error) {
      const errorCode = error.response?.data?.code;
      let message = t("reset_password.error.server_error");
      
      if (errorCode === "INVALID_TOKEN") {
        message = t("reset_password.error.invalid_token");
      }
      
      toast({
        title: t("reset_password.error.title"),
        description: error.response?.data?.message
          ? translateBackendError(error.response.data.message, t, "reset_password.error.server_error")
          : message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <Box
        minH="100vh"
        w="100%"
        position="relative"
        overflow="hidden"
        bg="#0a0d14"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="#4ade80" thickness="4px" />
      </Box>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      overflow="hidden"
      bg="#0a0d14"
    >
      {/* Animated Background */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.15) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        
        {/* Floating orbs */}
        <Box
          position="absolute"
          top="10%"
          left="5%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        
        {/* Grid pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />

        {/* Animated lines */}
        <Box
          position="absolute"
          top="30%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
      </Box>

      {/* Main Content */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        align="center"
        justify="center"
        p={{ base: 4, md: 8 }}
      >
        <Box
          as="form"
          onSubmit={handleSubmit}
          w="100%"
          maxW="480px"
          animation={`${slideUp} 0.8s ease-out`}
        >
          {/* Glass Card */}
          <Box
            position="relative"
            p={{ base: 8, md: 12 }}
            borderRadius="24px"
            bg="linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.6) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid rgba(34, 197, 94, 0.15)"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              h: "1px",
              background: "linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)",
            }}
          >
            {/* Logo & Header */}
            <Flex direction="column" align="center" mb={8}>
              <Box position="relative" p={4} mb={0}>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="150px"
                  h="150px"
                  borderRadius="full"
                  bg="radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, rgba(34, 197, 94, 0.1) 50%, transparent 70%)"
                  filter="blur(20px)"
                  animation={`${pulse} 3s ease-in-out infinite`}
                />
                <img
                  src={logo}
                  width="120px"
                  alt="logo"
                  style={{ position: "relative", zIndex: 1 }}
                />
              </Box>

              <Heading
                color="white"
                fontSize={{ base: "28px", md: "36px" }}
                fontWeight="bold"
                bgGradient="linear(to-r, #fff, #4ade80, #fff)"
                bgClip="text"
                backgroundSize="200% auto"
                animation={`${gradientMove} 5s linear infinite`}
                textAlign="center"
              >
                {t("reset_password.title")}
              </Heading>

              <Text mt={3} color="gray.400" fontSize="md" textAlign="center">
                {t("reset_password.subtitle")}
              </Text>
            </Flex>

            {/* Form Fields */}
            <Box>
              {/* New Password */}
              <FormControl mb={6}>
                <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                  {t("reset_password.new_password")}
                </FormLabel>
                <Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("reset_password.new_password")}
                  size="lg"
                  bg="rgba(15, 20, 30, 0.6)"
                  border="1px solid rgba(255, 255, 255, 0.1)"
                  borderRadius="16px"
                  color="white"
                  h="56px"
                  fontSize="md"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  _focus={{
                    border: "1px solid rgba(34, 197, 94, 0.6)",
                    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  transition="all 0.3s ease"
                />
                <PasswordStrengthBar password={newPassword} />
              </FormControl>

              {/* Confirm Password */}
              <FormControl mb={6}>
                <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                  {t("reset_password.confirm_password")}
                </FormLabel>
                <Input
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("reset_password.confirm_password")}
                  size="lg"
                  bg="rgba(15, 20, 30, 0.6)"
                  border="1px solid rgba(255, 255, 255, 0.1)"
                  borderRadius="16px"
                  color="white"
                  h="56px"
                  fontSize="md"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  _focus={{
                    border: "1px solid rgba(34, 197, 94, 0.6)",
                    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  transition="all 0.3s ease"
                />
              </FormControl>

              {/* Submit Button */}
              <MufynButton
                type="submit"
                size="xl"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                withRipple
              >
                {t("reset_password.reset_button")}
              </MufynButton>
            </Box>

            {/* Footer Link */}
            <Flex justify="center" mt={8}>
              <Link
                as={RouterLink}
                to="/auth/signin"
                color="#4ade80"
                fontSize="sm"
                fontWeight="semibold"
                _hover={{
                  color: "#22c55e",
                  textDecoration: "none",
                }}
                transition="color 0.2s ease"
              >
                ← {t("reset_password.back_to_login")}
              </Link>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default ResetPassword;
